import { Loader2, KeyRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { createTenantInvite } from "@/services/tenantUsersService";

function isValidEmail(value: string): boolean {
  return /.+@.+\..+/.test(value.trim());
}

export function TransferOwnershipCard({
  tenantId,
  onTransferRequested,
}: {
  tenantId: string;
  onTransferRequested?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = (): void => {
    setEmail("");
    setSubmitting(false);
  };

  const handleClose = (next: boolean): void => {
    if (!next) reset();
    setOpen(next);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!isValidEmail(email)) {
      toast.error(
        t(
          "users.transferOwnership.emailInvalid",
          "Ingresá un email válido.",
        ),
      );
      return;
    }
    setSubmitting(true);
    try {
      await createTenantInvite(tenantId, {
        email,
        role: "owner",
        transferOwnership: true,
      });
      toast.success(
        t(
          "users.transferOwnership.successToast",
          "Enviamos la invitación. La transferencia se completa cuando la persona acepta.",
        ),
      );
      handleClose(false);
      onTransferRequested?.();
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 409) {
        toast.error(
          t(
            "users.transferOwnership.alreadyMemberOrPending",
            "Ya existe una invitación o membresía para ese email.",
          ),
        );
      } else {
        toast.error(
          t(
            "users.transferOwnership.errorToast",
            "No pudimos enviar la invitación.",
          ),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--fx-accent-50)] text-[var(--fx-accent-700)]">
            <KeyRound className="h-4 w-4" aria-hidden />
          </div>
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-[var(--fx-ink-900)]">
              {t("users.transferOwnership.title", "Transferir propiedad")}
            </h3>
            <p className="text-xs text-[var(--fx-ink-600)]">
              {t(
                "users.transferOwnership.description",
                "Invitá al titular real de la empresa. Cuando acepte, queda como propietario y vos pasás a ser su contador.",
              )}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(true)}
          className="shrink-0"
        >
          {t(
            "users.transferOwnership.start",
            "Iniciar transferencia",
          )}
        </Button>
      </CardContent>

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t(
                "users.transferOwnership.dialog.title",
                "Transferir propiedad",
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                "users.transferOwnership.dialog.description",
                "Cuando la persona acepte, va a quedar como propietario y vos pasás a tener rol de contador. La acción no se puede deshacer desde acá.",
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="transfer-email"
              className="text-sm font-medium text-[var(--fx-ink-700)]"
            >
              {t(
                "users.transferOwnership.dialog.email",
                "Email del nuevo propietario",
              )}
            </label>
            <Input
              id="transfer-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="propietario@empresa.com"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleClose(false)}
              disabled={submitting}
            >
              {t("pages.common.cancel", "Cancelar")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || email.trim().length === 0}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : null}
              {t(
                "users.transferOwnership.dialog.submit",
                "Enviar invitación",
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
